// Reddit is EMBED_REMOTE (.specs/scrapers.md → Current sources): we store the
// post's canonical permalink and render Reddit's own embed. Reddit's text is
// never fetched or stored.

const PERMALINK = /^\/r\/([^/]+)\/comments\/([a-z0-9]+)(?:\/([^/]+))?/i;

/**
 * The canonical permalink of a Reddit post URL
 * (`https://www.reddit.com/r/<sub>/comments/<id>/<slug>/`), or null when the
 * URL is not a post on reddit.com. A `redd.it` short link is null: resolving it
 * needs a fetch.
 */
export function canonicalRedditUrl(url: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  const host = parsed.hostname.toLowerCase();
  if (host !== 'reddit.com' && !host.endsWith('.reddit.com')) return null;
  const match = PERMALINK.exec(parsed.pathname);
  if (!match) return null;
  const [, subreddit, id, slug] = match;
  return `https://www.reddit.com/r/${subreddit}/comments/${id!.toLowerCase()}/${slug ? `${slug}/` : ''}`;
}

/** The `src` of Reddit's official embed for a post permalink. */
export function redditEmbedSrc(
  permalink: string,
  theme: 'light' | 'dark'
): string | null {
  const canonical = canonicalRedditUrl(permalink);
  if (!canonical) return null;
  const src = new URL(canonical);
  src.hostname = 'embed.reddit.com';
  src.searchParams.set('embed', 'true');
  src.searchParams.set('theme', theme);
  return src.toString();
}

type PostWithSourceTags = {
  postFactiiisCount?:
    | { postFactiiis: { data: string | null; scrapeId: number | null }[] }[]
    | null;
};

/**
 * The Reddit post a scraped post embeds: the permalink in the data of its
 * source tag. Only a scraper sets `scrapeId`, so a user who pastes a Reddit
 * link into a tag gets no embed.
 */
export function redditPermalinkOf(post: PostWithSourceTags): string | null {
  for (const count of post.postFactiiisCount ?? []) {
    for (const tag of count.postFactiiis) {
      if (tag.scrapeId === null || !tag.data) continue;
      const permalink = canonicalRedditUrl(tag.data);
      if (permalink) return permalink;
    }
  }
  return null;
}
